import React from 'react';
import I18n from 'i18n-js';
import classNames from 'classnames';
import gql from 'graphql-tag';
import { withRouter, WithRouterProps } from 'react-router';
import { Location } from 'history';

import ConditionDisplay from './condition_display';
import PriceDisplay from './price_display';
import PriceWithShipping from './price_with_shipping';
import ImageCarousel from './image_carousel';
import ListingCardNudge from './listing_card_nudge';
import {
  ListingCardFields,
  FinancingFields,
  InOtherCartsCardData,
  core_apimessages_Listing_ListingType as ListingType,
  core_apimessages_Address as Address,
} from '../rql';
import AddToCartButton from './add_to_cart_button';
import WatchBadgeWithRQL from './watch_badge';
import CoreLink from './core_link';
import { isUsingProximityFeatures, ITEM_WITH_SLUG_ROUTE, listingItemPagePath } from '../url_helpers';
import { withUserContext, IUserContext, IUser } from './user_context_provider';
import BumpTag, { shouldShowBumpTag } from './bump_tag';
import { buildListingClickEvent } from '../elog/mparticle_tracker';
import { WEB, US_CON } from '../constants';
import experiments from '../experiments';
import { formatExperimentsForEventAttributes } from '../elog/mparticle';
import { ListingOfferMetadata } from './listing_offer_metadata';
import ListingShopCountryNudge from './listing_shop_country_nudge';
import { BRAND_NEW } from '../condition_helpers';
import ListingShippingDisplay, { ListingShippingDisplayFragment } from './listing_shipping_display';
import { SellYoursButton } from './sell_yours_button';
import { isExperimentEnabled } from '../user_context_helpers';
import ListingCardSignals, { ListingWithSignals } from './listing_card_signals';

const DISPLAY_STYLE = 'squareCard';

type ListingCardListingFields = ListingCardFields.Fragment & FinancingFields.Fragment & InOtherCartsCardData.Fragment & ListingWithSignals;

interface IProps {
  // This type is likely _more_ than what this component needs.
  // Cutting a fragment for this component would help better communicate
  // the contract here.
  listing?: ListingCardListingFields;
  position?: number;
  inMobileApp?: boolean;
  soldListing?: boolean;
  footerContent?: React.ReactNode;
  hideMetadataAsOffer?: boolean;
  trackingName?: string;
  displayAddToCart?: boolean;
  listingsCount?: number;
  combinedShippingDiscount?: boolean;
  showEstimatedMonthlyPayment?: boolean;
  trackingQuery?: string;
  showFlag?: boolean;
  showShippingPrice?: boolean;
}

export class ListingCard extends React.Component<IProps & IUserContext & WithRouterProps, null> {
  isBumped() {
    return shouldShowBumpTag(this.props.listing);
  }

  isFreeExpeditedShipping() {
    return !!this.props.listing.shipping?.freeExpeditedShipping;
  }

  isFreeShipping() {
    return this.props.listing.shipping?.shippingPrices?.[0]?.rate?.amountCents === 0;
  }

  isLocalPickupOnly() {
    return !!this.props.listing.shipping?.localPickupOnly;
  }

  isLocalPickupAvailable() {
    const { listing: { shipping, shop }, user, location } = this.props;
    const localPickupAvailable = !!shipping?.localPickup && !this.isLocalPickupOnly();
    if (!localPickupAvailable) return false;
    if (!showProximityContent(shop?.address, user, location)) return false;
    return true;
  }

  isGreatValue() {
    const { listing: { pricing, priceRecommendation, condition, shop, currency }, user } = this.props;
    const priceEstimate = priceRecommendation?.priceMiddle;
    const priceActual = pricing.buyerPrice;
    
    if (showGreatValueUK(user, currency, priceActual) || showGreatValueUS(user, currency, priceActual, condition, shop)) {
      return priceEstimate &&
        priceActual &&
        priceEstimate.currency === priceActual.currency &&
        priceEstimate.amountCents >= priceActual.amountCents;
    }

    return false;
  }

  returnPolicyDays() {
    const { listing: { condition, shop, soldAsIs } } = this.props;
    if (soldAsIs || !condition?.conditionSlug) return 0;
    const returnPolicyDays = condition.conditionSlug === BRAND_NEW ? shop.returnPolicy?.newReturnWindowDays : shop.returnPolicy?.usedReturnWindowDays;
    return (returnPolicyDays || 0);
  }

  listingUrl() {
    if (this.props.soldListing) {
      return (
        ITEM_WITH_SLUG_ROUTE.expand({
          slug: this.props.listing.slug,
          id: this.props.listing.id,
          show_sold: true,
        })
      );
    }

    return (
      listingItemPagePath(this.props.listing)
    );
  }

  hasDiscount() {
    return !!this.props.listing.pricing.originalPrice;
  }

  ribbonText() {
    return this.props.listing.pricing.ribbon ?
      this.props.listing.pricing.ribbon.display :
      null;
  }

  renderOriginalPrice() {
    if (!this.hasDiscount()) { return null; }

    return (
      <div className="grid-card__original-price">
        <span className="mr-space td-line-through">
          {this.props.listing.pricing.originalPrice.display}
        </span>
        <span className="grid-card__discount">
          {this.ribbonText()}
        </span>
      </div>
    );
  }

  renderMetadata() {
    if (this.props.hideMetadataAsOffer) return null;

    return (
      <ListingOfferMetadata listing={this.props.listing} />
    );
  }

  clickEvent() {
    return {
      experiments: formatExperimentsForEventAttributes(
        this.props.user,
        [
          experiments.LISTING_CARD_LOCATION,
          experiments.DUMMY,
          experiments.DATA_DRIVEN_WELL_PRICED_LISTINGS,
          experiments.GREAT_VALUE_BADGE_UK,
        ],
      ),
      query: this.props.trackingQuery,
      shownAsBumped: this.isBumped(),
      ...buildListingClickEvent({
        listing: this.props.listing,
        componentName: this.props.trackingName,
        displayStyle: DISPLAY_STYLE,
        position: this.props.position,
        listingsCount: this.props.listingsCount,
        city: this.props.listing.shop.address?.locality,
        region: this.props.listing.shop.address?.region,
        countryCode: this.sellerCountryCode,
        isListingDomesticToBuyer: isListingDomesticToBuyer(this.sellerCountryCode, this.props.user?.countryCode),
      }),
    };
  }

  get sellerCountryCode() {
    return this.props.listing.shop.address?.country?.countryCode;
  }

  renderProximityShopLocation() {
    const shopAddress = this.props.listing.shop?.address;
    if (!shopAddress) return null;

    if (showProximityContent(shopAddress, this.props.user, this.props.location)) {
      return (
        <div data-proximity-location className="size-80 color-secondary">
          {I18n.t('commons.listingCard.location.sameCountryUs', {
            city: shopAddress.locality, state: shopAddress.region,
          })}
        </div>
      );
    }

    return null;
  }

  renderShopLocationCountryFlag() {
    const shopAddress = this.props.listing.shop?.address;
    if (!shopAddress || !shopAddress.country) return null;

    if (this.props.showFlag || showFlagForMarketplace(this.props.user, this.props.location)) {
      return (
        <div>
          <ListingShopCountryNudge
            data-shop-country-nudge
            country={shopAddress.country}
          />
        </div>
      );
    }

    return null;
  }

  renderNudges() {
    const { listing } = this.props;

    if (this.props.listing.signals) {
      return <ListingCardSignals listing={listing} />;
    }

    return (
      <ListingCardNudge
        freeExpeditedShipping={this.isFreeExpeditedShipping()}
        freeShipping={this.isFreeShipping()}
        greatValue={this.isGreatValue()}
        inOtherCartsCount={listing?.otherBuyersWithListingInCartCounts}
        localPickupAvailable={this.isLocalPickupAvailable()}
        localPickupOnly={this.isLocalPickupOnly()}
        returnPolicyDays={this.returnPolicyDays()}
        displayMultiple
      />
    );
  }

  renderSellYoursButton() {
    const { listing, soldListing, user } = this.props;
    const isEnabled = isExperimentEnabled(user, experiments.SELL_YOURS_SOLD_LISTINGS_SEARCH);

    return isEnabled && soldListing && (
      <div className="mt-1 mobile-mb-1">
        <SellYoursButton listingId={listing?.id} />
      </div>
    );
  }

  render() {
    const { listing, user } = this.props;
    if (!listing) {
      return <PlaceholderCard />;
    }

    const classes = classNames(
      'grid-card',
      { 'grid-card--software': isDigital(listing) },
      { 'grid-card--discounted': this.hasDiscount() },
      { 'grid-card--with-cart-action': this.props.displayAddToCart },
    );

    const openNewTab = user && user.deviceName === WEB;
    const images = listing.images.map(i => (i.source));

    return (
      <div className={classes}>
        <CoreLink
          className="grid-card__inner"
          to={this.listingUrl()}
          target={openNewTab ? '_blank' : undefined}
          clickEvent={this.clickEvent()}
        >
          {this.renderMetadata()}
          <div className="grid-card__main">
            <div className="grid-card__image">
              <ImageCarousel
                images={images}
              />
              {this.isBumped() &&
                <div className="grid-card__bump-tag">
                  <BumpTag />
                </div>
              }
              {listing.state !== 'live' &&
                <div className="grid-card__sold">
                  {listing.stateDescription}
                </div>
              }
            </div>
            <div className="grid-card__main__text">
              <h4 className="grid-card__title">
                {this.props.listing.title}
              </h4>
              {this.renderProximityShopLocation()}
              {this.props.showShippingPrice &&
                <ListingCondition listing={listing} />
              }
            </div>
          </div>
          <div className="grid-card__footer">
            {this.renderOriginalPrice()}
            <div
              className="grid-card__footer__pricing"
            >
              <div className="grid-card__price">
                <ListingPrice
                  combinedShippingDiscount={this.props.combinedShippingDiscount}
                  showShippingPrice={this.props.showShippingPrice}
                  listing={listing}
                />
                {this.props.showShippingPrice &&
                  <ListingShippingDisplay listing={this.props.listing} onlyShowCost />
                }
              </div>
              {!this.props.showShippingPrice &&
                <ListingCondition listing={listing} />
              }
            </div>
            {this.renderShopLocationCountryFlag()}
            {this.renderNudges()}
            {this.props.footerContent}
          </div>
        </CoreLink>
        {this.props.displayAddToCart &&
          <AddToCartButton
            listing={this.props.listing}
            className="width-100"
            position={this.props.position}
            parentComponentName={this.props.trackingName}
          />
        }
        {this.renderSellYoursButton()}
        <div className="grid-card__watch">
          <WatchBadgeWithRQL
            listing={this.props.listing}
            displayStyle="icon"
          />
        </div>
      </div>
    );
  }
}

function showGreatValueUS(
  user: Partial<IUser>,
  currency: string,
  priceActual: ListingCardFields.BuyerPrice,
  condition: ListingCardFields.Condition,
  shop: ListingCardFields.Shop,
): boolean {
  if (!condition?.conditionSlug) return false;
  if (user?.shippingRegionCode !== US_CON) return false;
  if (currency !== 'USD' && currency) return false; // the listing must be listed in USD
  if (priceActual?.currency !== 'USD') return false; // and the buyer must also be using USD
  if (condition.conditionSlug === BRAND_NEW && shop.address?.country?.countryCode === 'US') {
    return false; // We have an issue with representing MAP-priced items as great values, so we've added this hack to fix that
  }
  return true;
}

function showGreatValueUK(user: Partial<IUser>, currency: string, priceActual: ListingCardFields.BuyerPrice): boolean {
  if (
    user?.shippingRegionCode === 'GB'
    && currency === 'GBP'
    && priceActual?.currency === 'GBP'
    && isExperimentEnabled(user, experiments.GREAT_VALUE_BADGE_UK)
  ) return true;
  return false;
}

function isListingDomesticToBuyer(sellerCountryCode?: string, buyerCountryCode?: string): boolean {
  if (!(sellerCountryCode && buyerCountryCode)) { return false; }
  return sellerCountryCode === buyerCountryCode;
}

function showFlagForMarketplace(user: Partial<IUser>, location: Location) {
  return user.countryCode !== 'US' && optionalPathnameIncludes(location, '/marketplace');
}

function optionalPathnameIncludes(location: Location, path: string): boolean {
  if (!location?.pathname) {
    return false;
  }

  return location?.pathname?.includes(path);
}

function showProximityContent(shopAddress: Address, user: Partial<IUser>, location: Location) {
  return isUsingProximityFeatures(location) &&
    shopAddress?.country?.countryCode === 'US' &&
    user.countryCode === 'US';
}

function ListingPrice(props: {
  listing: ListingCardListingFields,
  combinedShippingDiscount: boolean,
  showShippingPrice: boolean,
}) {
  const { listing, combinedShippingDiscount } = props;

  if (combinedShippingDiscount) {
    return (
      <PriceWithShipping
        listing={listing}
        showCombinedRate={combinedShippingDiscount}
        useCombinedShippingStrikethrough={combinedShippingDiscount}
      />
    );
  }

  return (
    <PriceDisplay
      amountCents={listing.pricing.buyerPrice.amountCents}
      currency={listing.pricing.buyerPrice.currency}
      display={listing.pricing.buyerPrice.display}
      auction={isAuction(listing)}
    />
  );
}

function ListingCondition(props: { listing: ListingCardListingFields }) {
  const { listing } = props;
  if (isDigital(listing)) return null;
  if (!listing.condition) return null;

  return (
    <div className="grid-card__condition">
      <ConditionDisplay
        slug={listing.condition.conditionSlug}
        displayName={listing.condition.displayName}
        textOnly
      />
    </div>
  );
}

function PlaceholderCard() {
  return (
    <div className="grid-card grid-card--placeholder">
      <div className="grid-card__inner">
        <div className="grid-card__main">
          <div className="grid-card__image" />
          <div className="grid-card__main__text">
            <div className="grid-card__title" />
          </div>
        </div>

        <div className="grid-card__footer">
          <div className="grid-card__footer__pricing">
            <div className="grid-card__price" />
          </div>
        </div>
      </div>
    </div>
  );
}

function isDigital(listing: ListingCardListingFields) {
  return listing.listingType === ListingType.DIGITAL;
}

function isAuction(listing: ListingCardListingFields) {
  return listing.listingType === ListingType.AUCTION;
}

export const ListingCardFragment = gql`
  fragment ListingCardFields on Listing {
    _id
    id
    title
    slug
    listingType
    upc
    state
    stateDescription
    bumped
    watching
    soldAsIs
    condition {
      displayName
      conditionSlug
      conditionUuid
    }
    pricing {
      buyerPrice {
        display
        currency
        amount
        amountCents
      }
      originalPrice {
        display
      }
      ribbon {
        display
      }
    }
    images(input: {
      transform: "card_square",
      count: 3,
      scope: "photos",
      type: "Product"
    }) {
      source
    }
    shipping {
      shippingPrices {
        shippingMethod
        carrierCalculated
        destinationPostalCodeNeeded
        rate {
          amount
          amountCents
          currency
          display
        }
      }
      freeExpeditedShipping
      localPickupOnly
      localPickup
    }
    shop {
      _id
      returnPolicy {
        usedReturnWindowDays
        newReturnWindowDays
      }
      address {
        locality
        region
        country {
          countryCode
          name
        }
      }
    }
    priceRecommendation {
      priceMiddle {
        amountCents
        currency
      }
    }
    currency
    ...ListingShippingDisplayData
  }
  ${ListingShippingDisplayFragment}
`;

export default withRouter(withUserContext(ListingCard));
